import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Separator } from "@/components/ui/separator";
import { 
  createFallbackPurchase, 
  getUserDigitalContentPurchases, 
  diagnosePurchase,
  type PendingPurchaseInfo 
} from "@/utils/fixPendingPurchases";
import { toast } from "sonner";
import { 
  AlertTriangle, 
  CheckCircle, 
  RefreshCw, 
  Search, 
  Plus, 
  User, 
  FileText,
  DollarSign
} from "lucide-react";

export default function DigitalContentPurchaseDebug() {
  const [loading, setLoading] = useState(false);
  const [diagnosis, setDiagnosis] = useState<any>(null);
  const [userPurchases, setUserPurchases] = useState<any[]>([]);
  
  // Estados para diagnóstico
  const [diagnosisForm, setDiagnosisForm] = useState({
    contentId: "3fe6ba0e-f8d9-4888-9e21-36ed7e57d95f", // ID da imagem
    buyerId: "f769f790-0333-4214-a194-0cdbc701f88b"    // ID da imagem
  });

  // Estados para correção manual
  const [fixForm, setFixForm] = useState<PendingPurchaseInfo>({
    contentId: "3fe6ba0e-f8d9-4888-9e21-36ed7e57d95f",
    buyerId: "f769f790-0333-4214-a194-0cdbc701f88b",
    contentTitle: "teste compra",
    buyerEmail: "ju.vanim@hotmail.com",
    priceInCents: 200
  });

  const handleDiagnose = async () => {
    if (!diagnosisForm.contentId || !diagnosisForm.buyerId) {
      toast.error("Preencha o Content ID e Buyer ID");
      return;
    }

    setLoading(true);
    try {
      const result = await diagnosePurchase(diagnosisForm.contentId, diagnosisForm.buyerId);
      
      if (result.success) {
        setDiagnosis(result.data);
        toast.success("Diagnóstico concluído!");
      } else {
        toast.error(`Erro no diagnóstico: ${result.error}`);
      }
    } catch (error) {
      toast.error("Erro inesperado no diagnóstico");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleFixPurchase = async () => {
    if (!fixForm.contentId || !fixForm.buyerId) {
      toast.error("Preencha todos os campos obrigatórios");
      return;
    }

    setLoading(true);
    try {
      const result = await createFallbackPurchase(fixForm);
      
      if (result.success) {
        toast.success(`Compra ${result.action === 'created' ? 'criada' : 'atualizada'} com sucesso!`);
        
        // Recarregar diagnóstico se estiver na mesma compra
        if (diagnosisForm.contentId === fixForm.contentId && diagnosisForm.buyerId === fixForm.buyerId) {
          handleDiagnose();
        }
      } else {
        toast.error(`Erro ao corrigir compra: ${result.error}`);
      }
    } catch (error) {
      toast.error("Erro inesperado ao corrigir compra");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleGetUserPurchases = async () => {
    if (!diagnosisForm.buyerId) {
      toast.error("Preencha o Buyer ID");
      return;
    }

    setLoading(true);
    try {
      const result = await getUserDigitalContentPurchases(diagnosisForm.buyerId);
      
      if (result.success) {
        setUserPurchases(result.data || []);
        toast.success(`${result.data?.length || 0} compras encontradas`);
      } else {
        toast.error(`Erro ao buscar compras: ${result.error}`);
      }
    } catch (error) {
      toast.error("Erro inesperado ao buscar compras");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6 p-6">
      <div className="text-center">
        <h1 className="text-2xl font-bold mb-2">Debug de Compras de Conteúdos Digitais</h1>
        <p className="text-gray-600">Ferramenta para diagnosticar e corrigir problemas com webhooks do Stripe</p>
      </div>

      {/* Seção de Diagnóstico */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Search className="h-5 w-5" />
            Diagnóstico de Compra
          </CardTitle>
          <CardDescription>
            Verificar se uma compra específica existe e está configurada corretamente
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="contentId">Content ID</Label>
              <Input
                id="contentId"
                value={diagnosisForm.contentId}
                onChange={(e) => setDiagnosisForm(prev => ({ ...prev, contentId: e.target.value }))}
                placeholder="UUID do conteúdo digital"
              />
            </div>
            <div>
              <Label htmlFor="buyerId">Buyer ID</Label>
              <Input
                id="buyerId"
                value={diagnosisForm.buyerId}
                onChange={(e) => setDiagnosisForm(prev => ({ ...prev, buyerId: e.target.value }))}
                placeholder="UUID do comprador"
              />
            </div>
          </div>
          
          <div className="flex gap-2">
            <Button onClick={handleDiagnose} disabled={loading}>
              {loading ? <RefreshCw className="h-4 w-4 animate-spin mr-2" /> : <Search className="h-4 w-4 mr-2" />}
              Diagnosticar
            </Button>
            <Button variant="outline" onClick={handleGetUserPurchases} disabled={loading}>
              <User className="h-4 w-4 mr-2" />
              Ver Todas as Compras do Usuário
            </Button>
          </div>

          {/* Resultado do Diagnóstico */}
          {diagnosis && (
            <div className="mt-4 p-4 bg-gray-50 rounded-lg">
              <h3 className="font-semibold mb-3">Resultado do Diagnóstico:</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                <div className="flex items-center gap-2">
                  {diagnosis.contentExists ? <CheckCircle className="h-4 w-4 text-green-500" /> : <AlertTriangle className="h-4 w-4 text-red-500" />}
                  <span className={diagnosis.contentExists ? "text-green-700" : "text-red-700"}>
                    Conteúdo {diagnosis.contentExists ? "Existe" : "Não Existe"}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  {diagnosis.userExists ? <CheckCircle className="h-4 w-4 text-green-500" /> : <AlertTriangle className="h-4 w-4 text-red-500" />}
                  <span className={diagnosis.userExists ? "text-green-700" : "text-red-700"}>
                    Usuário {diagnosis.userExists ? "Existe" : "Não Existe"}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  {diagnosis.purchaseExists ? <CheckCircle className="h-4 w-4 text-green-500" /> : <AlertTriangle className="h-4 w-4 text-red-500" />}
                  <span className={diagnosis.purchaseExists ? "text-green-700" : "text-red-700"}>
                    Compra {diagnosis.purchaseExists ? "Existe" : "Não Existe"} ({diagnosis.purchaseCount})
                  </span>
                </div>
              </div>

              {diagnosis.contentData && (
                <div className="mb-3">
                  <Badge variant="outline" className="mb-2">Dados do Conteúdo</Badge>
                  <p className="text-sm"><strong>Título:</strong> {diagnosis.contentData.title}</p>
                  <p className="text-sm"><strong>Preço:</strong> R$ {(diagnosis.contentData.price_cents / 100).toFixed(2)}</p>
                </div>
              )}

              {diagnosis.userData && (
                <div className="mb-3">
                  <Badge variant="outline" className="mb-2">Dados do Usuário</Badge>
                  <p className="text-sm"><strong>Nickname:</strong> {diagnosis.userData.nickname}</p>
                  <p className="text-sm"><strong>Email:</strong> {diagnosis.userData.email}</p>
                </div>
              )}

              {diagnosis.purchaseData && diagnosis.purchaseData.length > 0 && (
                <div>
                  <Badge variant="outline" className="mb-2">Compras Encontradas</Badge>
                  {diagnosis.purchaseData.map((purchase: any, index: number) => (
                    <div key={index} className="text-sm mb-2 p-2 bg-white rounded">
                      <p><strong>Status:</strong> <Badge variant={purchase.payment_status === 'completed' ? 'default' : 'destructive'}>{purchase.payment_status}</Badge></p>
                      <p><strong>Método:</strong> {purchase.payment_method}</p>
                      <p><strong>Acesso:</strong> {purchase.access_granted_at ? "✅ Liberado" : "❌ Não liberado"}</p>
                      <p><strong>Criado:</strong> {new Date(purchase.created_at).toLocaleString()}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      <Separator />

      {/* Seção de Correção Manual */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Plus className="h-5 w-5" />
            Correção Manual de Compra
          </CardTitle>
          <CardDescription>
            Criar ou atualizar registro de compra quando o webhook falha
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Alert>
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              Use esta função apenas quando tiver certeza de que o pagamento foi aprovado no Stripe mas o webhook falhou.
            </AlertDescription>
          </Alert>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="fixContentId">Content ID</Label>
              <Input
                id="fixContentId"
                value={fixForm.contentId}
                onChange={(e) => setFixForm(prev => ({ ...prev, contentId: e.target.value }))}
                placeholder="UUID do conteúdo digital"
              />
            </div>
            <div>
              <Label htmlFor="fixBuyerId">Buyer ID</Label>
              <Input
                id="fixBuyerId"
                value={fixForm.buyerId}
                onChange={(e) => setFixForm(prev => ({ ...prev, buyerId: e.target.value }))}
                placeholder="UUID do comprador"
              />
            </div>
            <div>
              <Label htmlFor="fixContentTitle">Título do Conteúdo</Label>
              <Input
                id="fixContentTitle"
                value={fixForm.contentTitle}
                onChange={(e) => setFixForm(prev => ({ ...prev, contentTitle: e.target.value }))}
                placeholder="Nome do conteúdo"
              />
            </div>
            <div>
              <Label htmlFor="fixBuyerEmail">Email do Comprador</Label>
              <Input
                id="fixBuyerEmail"
                value={fixForm.buyerEmail}
                onChange={(e) => setFixForm(prev => ({ ...prev, buyerEmail: e.target.value }))}
                placeholder="email@exemplo.com"
              />
            </div>
            <div>
              <Label htmlFor="fixPrice">Preço (em centavos)</Label>
              <Input
                id="fixPrice"
                type="number"
                value={fixForm.priceInCents}
                onChange={(e) => setFixForm(prev => ({ ...prev, priceInCents: parseInt(e.target.value) || 0 }))}
                placeholder="200 = R$ 2,00"
              />
            </div>
          </div>

          <Button onClick={handleFixPurchase} disabled={loading} className="w-full">
            {loading ? <RefreshCw className="h-4 w-4 animate-spin mr-2" /> : <Plus className="h-4 w-4 mr-2" />}
            Corrigir Compra
          </Button>
        </CardContent>
      </Card>

      {/* Lista de Compras do Usuário */}
      {userPurchases.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Compras do Usuário ({userPurchases.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {userPurchases.map((purchase: any, index: number) => (
                <div key={index} className="p-3 border rounded-lg">
                  <div className="flex justify-between items-start mb-2">
                    <h4 className="font-medium">{purchase.digital_content?.title || "Título não encontrado"}</h4>
                    <Badge variant={purchase.payment_status === 'completed' ? 'default' : 'destructive'}>
                      {purchase.payment_status}
                    </Badge>
                  </div>
                  <div className="text-sm text-gray-600 space-y-1">
                    <p><strong>ID:</strong> {purchase.id}</p>
                    <p><strong>Preço:</strong> R$ {(purchase.purchase_price_cents / 100).toFixed(2)}</p>
                    <p><strong>Método:</strong> {purchase.payment_method}</p>
                    <p><strong>Acesso:</strong> {purchase.access_granted_at ? "✅ Liberado" : "❌ Pendente"}</p>
                    <p><strong>Data:</strong> {new Date(purchase.created_at).toLocaleString()}</p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
} 